import React, { useState } from "react";
import {
  SubscriptionDetailModel,
  RelatedInvoicesGridModel,
  CancelSubscriptionModel,
  PauseSubscriptionModel,
} from "../../../shared/models";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  Input,
  ModalComponent,
  RedButton,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { SubscriptionService } from "../../../shared/services";
import {
  CancelSubscriptionTypes,
  InvoiceStatus,
  ItemBillingEnds,
  PauseDurationTypes,
  PauseSubscriptionOutStandingInvoiceActions,
  RefundSubscriptionTypes,
  SubscriptionStatus,
} from "../../../shared/enums";
import { SubscriptionUtil } from "../../../shared/utils";
import { InvoicePopupWrapper } from "../../../shared/styles";
import { RenderContactInfo } from "../../../shared/components";
import { AccountSettingsStore } from "../../../shared/store";

type IProps = {
  navigate: NavigateFunction;
  onClose: (refresh: boolean) => void;
  isCancel: boolean;
  subscriptionDetails: SubscriptionDetailModel;
  subscriptionId?: string;
  showInvoices?: boolean;
  invoicesData?: RelatedInvoicesGridModel[];
};

const CancelSubscriptionPopup: React.FC<IProps> = ({
  onClose,
  isCancel,
  subscriptionDetails,
  subscriptionId,
  showInvoices,
  invoicesData,
}) => {
  const [isAPIRequestPending, setAPIRequestPending] = React.useState(false);
  const [cancelData, setCancelData] = useState<CancelSubscriptionModel>(
    new CancelSubscriptionModel({
      lastPayment: subscriptionDetails.lastPayment,
    })
  );
  const [pauseData, setPauseData] = useState<PauseSubscriptionModel>(
    new PauseSubscriptionModel()
  );

  function row(label: string, rightSide: React.ReactNode, className?: string) {
    {
      return (
        <FlexContainer spacing={30} margin="15px">
          <CustomLabel className={className}>{label}</CustomLabel>
          <div>
            <FlexContainer alignItems="center" spacing={20}>
              {rightSide}
            </FlexContainer>
          </div>
        </FlexContainer>
      );
    }
  }

  function onCancel() {
    if (isAPIRequestPending) return;
    setAPIRequestPending(true);
    if (isCancel) {
      const isValid = cancelData.validate();
      if (isValid) {
        const cancelAPIModel = cancelData.createCancelAPIModel(
          subscriptionId || ""
        );
        SubscriptionService.cancelSubscription(cancelAPIModel).subscribe({
          next: (response) => {
            setAPIRequestPending(false);
            /* The API now reports the refund outcome (security findings C08/F06). The success toast used to
               show unconditionally, even when the refund failed at the gateway. */
            if (response.result?.success) {
              ToastService.showSuccess("Subscription Cancelled Successfully");
            } else if (response.result?.failureReason?.startsWith("RefundFailed")) {
              ToastService.showError(
                "Subscription cancelled, but the refund did not go through. Check the payment and try the refund again."
              );
            } else {
              ToastService.showError("Error in cancelling Subscription");
            }
            onClose(true);
          },
          error: () => {
            setAPIRequestPending(false);
            ToastService.showError("Error in cancelling Subscription");
          },
        });
      } else {
        setAPIRequestPending(false);
        setCancelData((prev) => new CancelSubscriptionModel({ ...prev }));
      }
    } else {
      const isValid = pauseData.validate();
      if (isValid) {
        const pauseAPIModel = pauseData.createPauseAPIModel(
          subscriptionId || ""
        );
        SubscriptionService.pauseSubscription(pauseAPIModel).subscribe({
          next: (response) => {
            setAPIRequestPending(false);
            if (response.result.success) {
              ToastService.showSuccess("Subscription Paused Successfully");
            }
            onClose(true);
          },
          error: () => {
            setAPIRequestPending(false);
            ToastService.showError("Error in pausing Subscription");
          },
        });
      } else {
        setAPIRequestPending(false);
        setPauseData((prev) => new PauseSubscriptionModel({ ...prev }));
      }
    }
  }

  const handleCancelInputChange = (
    field: keyof CancelSubscriptionModel,
    value:
      | keyof typeof CancelSubscriptionTypes
      | keyof typeof RefundSubscriptionTypes
      | Date
  ) => {
    let refundType = cancelData.refundType;

    switch (field) {
      case "cancelType":
        if (value === CancelSubscriptionTypes.Immediately) {
          refundType = RefundSubscriptionTypes.None;
        }
        break;
      case "refundType":
        refundType = value as keyof typeof RefundSubscriptionTypes;
        break;
    }

    setCancelData((prev) => {
      return new CancelSubscriptionModel({
        ...prev,
        [field]: value,
        refundType: refundType,
      });
    });
  };

  function onRefundPayment(paymentTransactionId: string) {
    setCancelData((prev) => {
      return new CancelSubscriptionModel({
        ...prev,
        refundType: RefundSubscriptionTypes.PaymentTransaction,
        refundPaymentTransactionId: paymentTransactionId,
      });
    });
  }

  const handlePauseInputChange = (
    field: keyof PauseSubscriptionModel,
    value:
      | keyof typeof PauseDurationTypes
      | keyof typeof PauseSubscriptionOutStandingInvoiceActions
      | Date
  ) => {
    setPauseData((prev) => {
      return new PauseSubscriptionModel({ ...prev, [field]: value });
    });
  };

  const radio = (
    value: boolean,
    fieldName: keyof CancelSubscriptionModel | keyof PauseSubscriptionModel,
    label: React.ReactNode,
    onChange: () => void
  ) => {
    return (
      <Input
        value={value}
        id={`rdb_${fieldName}`}
        type="radio"
        label={label}
        onChange={onChange}
        className={"chkbox-no-margin"}
      />
    );
  };

  const renderCancelContent = () => {
    return (
      <>
        {row(
          "Cancel Subscription",
          <div>
            <FlexContainer fxLayout="column" spacing={10} fxWrap="wrap">
              {radio(
                cancelData.cancelType === CancelSubscriptionTypes.Immediately,
                "cancelType",
                <>
                  Immediately on
                  <span className="semi-bold">
                    {" "}
                    {UtilityMethods.formatDateInString(new Date())}
                  </span>
                </>,
                () =>
                  handleCancelInputChange(
                    "cancelType",
                    CancelSubscriptionTypes.Immediately
                  )
              )}
              {subscriptionDetails.paymentDueDate &&
                radio(
                  cancelData.cancelType ===
                    CancelSubscriptionTypes.EndOfCurrentBillingPeriod,
                  "cancelType",
                  <>
                    End of current billing period on
                    <span className="semi-bold">
                      {" "}
                      {UtilityMethods.formatDateInString(
                        subscriptionDetails.paymentDueDate
                      )}{" "}
                    </span>
                  </>,
                  () =>
                    handleCancelInputChange(
                      "cancelType",
                      CancelSubscriptionTypes.EndOfCurrentBillingPeriod
                    )
                )}
              <div>
                <FlexContainer alignItems="center" spacing={10}>
                  {radio(
                    cancelData.cancelType ===
                      CancelSubscriptionTypes.OnCustomDate,
                    "cancelType",
                    "On a Custom Date",
                    () =>
                      handleCancelInputChange(
                        "cancelType",
                        CancelSubscriptionTypes.OnCustomDate
                      )
                  )}
                  {cancelData.cancelType ===
                    CancelSubscriptionTypes.OnCustomDate && (
                    <Input
                      type={"date"}
                      value={cancelData.customRefundDate.toISOString()}
                      error={cancelData.hasError("customRefundDate")}
                      errorMessage={""}
                      className={`merchant-inputs`}
                      onChange={(e) =>
                        handleCancelInputChange(
                          "customRefundDate",
                          e.target.value
                        )
                      }
                      placeholder={"Enter date"}
                      onDateChange={(date) =>
                        handleCancelInputChange(
                          "customRefundDate",
                          new Date(date)
                        )
                      }
                    />
                  )}
                </FlexContainer>
              </div>
            </FlexContainer>
          </div>
        )}
        {cancelData.cancelType === CancelSubscriptionTypes.Immediately &&
          row(
            "Refund",
            <div>
              <FlexContainer fxLayout="column" spacing={10} fxWrap="wrap">
                {radio(
                  cancelData.refundType === RefundSubscriptionTypes.None,
                  "refundType",
                  "No Refund",
                  () =>
                    handleCancelInputChange(
                      "refundType",
                      RefundSubscriptionTypes.None
                    )
                )}
                {cancelData.lastPayment && (
                  <>
                    {radio(
                      cancelData.refundType ===
                        RefundSubscriptionTypes.PaymentTransaction,
                      "refundType",
                      <>
                        Last Payment{" "}
                        <span className="semi-bold">
                          $
                          {AccountSettingsStore.formatAmount(
                            cancelData.lastPayment.amount
                          )}
                        </span>{" "}
                        paid on{" "}
                        <span className="semi-bold">
                          {UtilityMethods.formatDateInString(
                            cancelData.lastPayment.createdDate
                          )}
                        </span>
                      </>,
                      () => onRefundPayment(cancelData.lastPayment?.id || "")
                    )}
                    <div>
                      <FlexContainer alignItems="center" spacing={15}>
                        {radio(
                          cancelData.refundType ===
                            RefundSubscriptionTypes.CustomAmount,
                          "refundType",
                          "Custom Amount",
                          () =>
                            handleCancelInputChange(
                              "refundType",
                              RefundSubscriptionTypes.CustomAmount
                            )
                        )}
                        {cancelData.refundType ===
                          RefundSubscriptionTypes.CustomAmount && (
                          <Input
                            type={"text"}
                            value={cancelData.customAmount}
                            error={cancelData.hasError("customAmount")}
                            errorMessage={""}
                            className={`styled-input`}
                            onChange={(e) =>
                              handleCancelInputChange(
                                "customAmount",
                                e.target.value
                              )
                            }
                            placeholder={"Enter amount"}
                          />
                        )}
                      </FlexContainer>
                    </div>
                  </>
                )}
              </FlexContainer>
            </div>
          )}

        <CustomLabel color="red">
          Cancelling Subscription will Cancel any Past Due and Scheduled
          Invoices with no balance due.
        </CustomLabel>
      </>
    );
  };

  const getPastDueInvoicesCount = () => {
    if (!invoicesData) return null;
    if (invoicesData.length > 0) {
      return invoicesData.filter(
        (invoice) =>
          invoice.status === ("PastDue" as keyof typeof InvoiceStatus)
      ).length;
    }
    return 0;
  };

  const getTotalPastDueAmount = () => {
    if (!invoicesData) return null;
    if (invoicesData.length > 0) {
      return invoicesData
        .filter(
          (invoice) =>
            invoice.status === ("PastDue" as keyof typeof InvoiceStatus)
        )
        .reduce(
          (total, invoice) => total + (invoice.netAmount - invoice.paidAmount),
          0
        );
    }
    return 0;
  };

  const renderPauseContent = () => {
    return (
      <>
        {row(
          "Pause Duration",
          <FlexContainer
            fxLayout="column"
            spacing={10}
            className="pause-duration-block"
          >
            {radio(
              pauseData.pauseDurationType ===
                PauseDurationTypes.UntilManuallyResumed,
              "pauseDurationType",
              "Until Manually Resumed",
              () =>
                handlePauseInputChange(
                  "pauseDurationType",
                  PauseDurationTypes.UntilManuallyResumed
                )
            )}
            <div>
              <FlexContainer alignItems="center" spacing={20}>
                {radio(
                  pauseData.pauseDurationType ===
                    PauseDurationTypes.ResumeOnDate,
                  "pauseDurationType",
                  "Resume On",
                  () =>
                    handlePauseInputChange(
                      "pauseDurationType",
                      PauseDurationTypes.ResumeOnDate
                    )
                )}{" "}
                {pauseData.pauseDurationType ===
                  PauseDurationTypes.ResumeOnDate && (
                  <Input
                    type={"date"}
                    minDate={new Date()}
                    value={pauseData.resumeDate.toISOString()}
                    error={pauseData.hasError("resumeDate")}
                    errorMessage={""}
                    className={`merchant-inputs`}
                    onChange={(e) =>
                      handlePauseInputChange("resumeDate", e.target.value)
                    }
                    placeholder={"Enter date"}
                    onDateChange={(date) =>
                      handlePauseInputChange("resumeDate", new Date(date))
                    }
                  />
                )}
              </FlexContainer>
            </div>
          </FlexContainer>
        )}

        {showInvoices &&
          row(
            "Outstanding Invoices",
            <FlexContainer
              alignItems="flex-start"
              fxLayout="column"
              spacing={15}
            >
              <CustomLabel>
                There are{" "}
                <b className="highlighted-black">
                  ${getPastDueInvoicesCount()}
                </b>{" "}
                past due Invoices with a total balance of {""}
                <b className="highlighted-black">${getTotalPastDueAmount()}</b>
              </CustomLabel>
              {radio(
                pauseData.outstandingInvoiceType ===
                  PauseSubscriptionOutStandingInvoiceActions.None,
                "outstandingInvoiceType",
                <span className="default-text">
                  Keep as Past Due and continue billing attempts
                </span>,
                () =>
                  handlePauseInputChange(
                    "outstandingInvoiceType",
                    PauseSubscriptionOutStandingInvoiceActions.None
                  )
              )}
              {radio(
                pauseData.outstandingInvoiceType ===
                  PauseSubscriptionOutStandingInvoiceActions.MarkUncollectible,
                "outstandingInvoiceType",
                <span className="default-text">
                  Mark as Uncollectible and temporarily stop billing attempts
                </span>,
                () =>
                  handlePauseInputChange(
                    "outstandingInvoiceType",
                    PauseSubscriptionOutStandingInvoiceActions.MarkUncollectible
                  )
              )}
              {radio(
                pauseData.outstandingInvoiceType ===
                  PauseSubscriptionOutStandingInvoiceActions.MarkCancelled,
                "outstandingInvoiceType",
                <span className="default-text">
                  Mark as Cancelled and close Invoices with no balance due
                </span>,
                () =>
                  handlePauseInputChange(
                    "outstandingInvoiceType",
                    PauseSubscriptionOutStandingInvoiceActions.MarkCancelled
                  )
              )}
            </FlexContainer>
          )}

        {showInvoices && (
          <CustomLabel color="red" textAlign="center">
            Pausing Subscription will Cancel any future Scheduled Invoices. If a
            Scheduled invoice is after the Pause Duration, it will not be
            affected.
            <br />
            <br />
            If an Invoice is Uncollectible, billing will only resume if a new
            Payment Method is added for this Customer.
          </CustomLabel>
        )}
      </>
    );
  };

  const renderContent = () => {
    const colors = SubscriptionUtil.getColorsByStatus(
      subscriptionDetails.status
    );
    return (
      <InvoicePopupWrapper>
        {row(
          "Customer",
          (subscriptionDetails.contact && (
            <RenderContactInfo contact={subscriptionDetails.contact} />
          )) ||
            "-"
        )}
        {row(
          "Subscription No.",
          <CustomLabel> {subscriptionDetails.subscriptionNumber}</CustomLabel>
        )}
        {row(
          "Status",
          <>
            <CustomButton
              color={colors.color}
              bgColor={colors.bgColor}
              minWidth={"90px"}
              borderRadius={"4px !important"}
              fontWeight={"600"}
            >
              {SubscriptionStatus[subscriptionDetails.status]}
            </CustomButton>
            {subscriptionDetails.pastDueDays > 0 && (
              <CustomLabel color="red" fontWeight={600}>
                {subscriptionDetails.pastDueDays} days
              </CustomLabel>
            )}
          </>,
          "status-lh"
        )}
        {row(
          "Payment Amount",
          <CustomLabel fontWeight={600}>
            ${AccountSettingsStore.formatAmount(subscriptionDetails.amount)} USD
          </CustomLabel>
        )}
        {row(
          "Next Payment Due",
          <CustomLabel fontWeight={600}>
            {subscriptionDetails.paymentDueDate
              ? UtilityMethods.formatDateInString(
                  subscriptionDetails.paymentDueDate
                )
              : "-"}
          </CustomLabel>
        )}
        {row(
          "Bill Every",
          <CustomLabel>
            {subscriptionDetails.billingIntervalCount}{" "}
            {subscriptionDetails.billingInterval}
          </CustomLabel>
        )}
        {row(
          "Billing Ends",
          <CustomLabel>
            {subscriptionDetails.billingEnds === ItemBillingEnds.After && (
              <>
                {" "}
                {subscriptionDetails.billingEndIntervalCount}{" "}
                {subscriptionDetails.billingEndsAfterInterval}{" "}
              </>
            )}

            {ItemBillingEnds[subscriptionDetails.billingEnds]}
          </CustomLabel>
        )}
        {isCancel ? renderCancelContent() : renderPauseContent()}
      </InvoicePopupWrapper>
    );
  };

  return (
    <ModalComponent
      title={isCancel ? "Cancel Subscription" : "Pause Subscription"}
      titleFontSize="22px"
      bodyPadding="40px 50px"
      onClose={() => onClose && onClose(false)}
      showFooter={true}
      wrapperWidth="900px"
      bodyBackgroundColor="#fff"
      footerHeight="auto"
      footerBackground="rgba(255, 0, 0, 0.1)"
      redHeader={true}
      blueHeader={false}
      footerContent={
        <FlexContainer alignItems="center" justifyContent="center" spacing={15}>
          <RedButton
            minWidth="120px"
            height="30px"
            onClick={() => onClose(false)}
          >
            Keep Subscription
          </RedButton>
          <RedButton
            primary
            disabled={isAPIRequestPending}
            minWidth="120px"
            height="30px"
            onClick={() => onCancel()}
          >
            {isCancel ? "Cancel Subscription" : "Pause Subscription"}
          </RedButton>
        </FlexContainer>
      }
    >
      {renderContent()}
    </ModalComponent>
  );
};

export default withRouter(observer(CancelSubscriptionPopup));
